import {metadaApi, promptBridgeBaseQuery, invalidateOnSuccess} from '@dms/api/base';

import {
  GetServiceCampaignHyundaiApiArg,
  GetServiceCampaignHyundaiApiResponse,
  GetVehicleServiceCampaignApiResponse,
  GetVehicleServiceCampaignApiArg,
  PostServiceCaseJobServiceCampaignApiArg,
  PostServiceCaseJobServiceCampaignApiResponse,
  PostServiceCaseOrderJobServiceCampaignApiArg,
  PostServiceCaseOrderJobServiceCampaignApiResponse,
  GetVehicleServiceCampaignApiResponseSchema,
  GetVehicleServiceCampaignApiArgSchema,
  PostServiceCaseJobServiceCampaignApiResponseSchema,
  PostServiceCaseJobServiceCampaignApiArgSchema,
  PostServiceCaseOrderJobServiceCampaignApiResponseSchema,
  PostServiceCaseOrderJobServiceCampaignApiArgSchema,
  GetServiceCampaignHyundaiApiResponseSchema,
  GetServiceCampaignHyundaiApiArgSchema,
} from './types';

export const metadaWorkshopServiceCampaignApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getVehicleServiceCampaign: build.query<
      GetVehicleServiceCampaignApiResponse,
      GetVehicleServiceCampaignApiArg
    >({
      query: (queryArg) => ({
        url: 'service/v1/service-campaigns',
        params: {vehicleId: queryArg.vehicleId},
        headers: queryArg.branchId ? {'X-Branch': queryArg.branchId} : undefined,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'vehicleCampaign', id: queryArg.vehicleId},
      ],
      extraOptions: {
        responseSchema: GetVehicleServiceCampaignApiResponseSchema,
        requestSchema: GetVehicleServiceCampaignApiArgSchema,
      },
    }),
    postServiceCaseJobServiceCampaign: build.mutation<
      PostServiceCaseJobServiceCampaignApiResponse,
      PostServiceCaseJobServiceCampaignApiArg
    >({
      queryFn: async (queryArg, api, extraOptions, baseQuery) => {
        if (queryArg.body.serviceCampaign.platformCode !== 'honda') {
          const result = await baseQuery({
            url: `service/v1/service-case/${queryArg.serviceCaseId}/service-campaign`,
            method: 'POST',
            body: queryArg.body,
            queryArg,
            headers: queryArg.branchId ? {'X-Branch': queryArg.branchId} : undefined,
          });
          if (result.error) {
            return {error: result.error};
          }
          return {data: null};
        }
        const result = await promptBridgeBaseQuery(
          {
            url: `/api/v1/service-campaigns/service-case/${queryArg.serviceCaseId}`,
            method: 'POST',
            body: queryArg.body,
            queryArg,
            headers: queryArg.branchId ? {'X-Branch': queryArg.branchId} : undefined,
          },
          api,
          extraOptions
        );
        if (result.error) {
          return {error: result.error};
        }
        return {data: null};
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg) => [
        {type: 'serviceJobs', id: queryArg.serviceCaseId},
        'vehicleCampaign',
      ]),
      extraOptions: {
        responseSchema: PostServiceCaseJobServiceCampaignApiResponseSchema,
        requestSchema: PostServiceCaseJobServiceCampaignApiArgSchema,
      },
    }),
    postServiceCaseOrderJobServiceCampaign: build.mutation<
      PostServiceCaseOrderJobServiceCampaignApiResponse,
      PostServiceCaseOrderJobServiceCampaignApiArg
    >({
      queryFn: async (queryArg, api, extraOptions, baseQuery) => {
        if (queryArg.body.serviceCampaign.platformCode !== 'honda') {
          const result = await baseQuery({
            url: `service/v1/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}/job/service-campaign`,
            method: 'POST',
            body: queryArg.body,
            queryArg,
            headers: queryArg.branchId ? {'X-Branch': queryArg.branchId} : undefined,
          });
          if (result.error) {
            return {error: result.error};
          }
          return {data: null};
        }
        const result = await promptBridgeBaseQuery(
          {
            url: `/api/v1/service-campaigns/service-case/${queryArg.serviceCaseId}/order/${queryArg.serviceOrderId}`,
            method: 'POST',
            body: queryArg.body,
            queryArg,
            headers: queryArg.branchId ? {'X-Branch': queryArg.branchId} : undefined,
          },
          api,
          extraOptions
        );
        if (result.error) {
          return {error: result.error};
        }
        return {data: null};
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg) => [
        {type: 'serviceOrders', id: queryArg.serviceCaseId},
        {type: 'serviceOrder', id: queryArg.serviceOrderId},
        {type: 'serviceOrderJobs', id: queryArg.serviceOrderId},
        {type: 'orderProfitability', id: queryArg.serviceOrderId},
        'checkout',
        'afsCheckout',
        'afsPaymentsList',
        'serviceOrderTotalAmount',
        'vehicleCampaign',
        {type: 'serviceOrderCheckoutRestriction', id: queryArg.serviceOrderId},
        'afsPaymentIssueRestriction',
      ]),
      extraOptions: {
        responseSchema: PostServiceCaseOrderJobServiceCampaignApiResponseSchema,
        requestSchema: PostServiceCaseOrderJobServiceCampaignApiArgSchema,
      },
    }),
    getServiceCampaignHyundaiApi: build.query<
      GetServiceCampaignHyundaiApiResponse,
      GetServiceCampaignHyundaiApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-campaign-hyundai/${queryArg.manufacturerActionNumber}`,
        queryArg,
      }),
      extraOptions: {
        responseSchema: GetServiceCampaignHyundaiApiResponseSchema,
        requestSchema: GetServiceCampaignHyundaiApiArgSchema,
      },
    }),
  }),
});

export const {
  useGetVehicleServiceCampaignQuery,
  usePostServiceCaseJobServiceCampaignMutation,
  usePostServiceCaseOrderJobServiceCampaignMutation,
  useGetServiceCampaignHyundaiApiQuery,
} = metadaWorkshopServiceCampaignApi;
